import { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import Modal from '@/components/base/Modal';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { useToast } from '@/components/base/Toast';

// ---------- Tipos ----------
interface Nomina {
  id: string;
  trabajadorId: string;
  trabajadorNombre: string;
  periodoInicio: string;
  periodoFin: string;
  diasTrabajados: number;
  salarioDiario: number;
  monto: number;
  estado: 'pendiente' | 'pagada';
}

interface Trabajador {
  id: string;
  nombre: string;
  salarioDiario: number;
}

interface NominaForm {
  trabajadorId: string;
  periodoInicio: string;
  periodoFin: string;
  diasTrabajados: string;
}

const emptyForm: NominaForm = { trabajadorId: '', periodoInicio: '', periodoFin: '', diasTrabajados: '' };

function formatMXN(monto: number): string {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(monto);
}

// ---------- API helpers ----------
const API_URL =  'https://api.airmex.mx/api/v1';

function getToken() {
  return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Error desconocido' }));
    throw new Error(error.detail || 'Error en la petición');
  }
  if (res.status === 204) return null;
  return res.json();
}

export default function NominasPage() {
  const { showToast } = useToast();
  const [nominas, setNominas] = useState<Nomina[]>([]);
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<NominaForm>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<Nomina | null>(null);

  // Carga inicial
  const loadData = async () => {
    try {
      setLoading(true);
      const [nominasData, trabajadoresData] = await Promise.all([
        fetchWithAuth('/nominas/'),
        fetchWithAuth('/trabajadores/'), // reutilizamos el endpoint de trabajadores
      ]);
      setNominas(nominasData);
      setTrabajadores(trabajadoresData);
    } catch (err: any) {
      showToast('error', 'Error al cargar datos', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Filtro
  const filtered = useMemo(
    () => nominas.filter((n) => n.trabajadorNombre.toLowerCase().includes(search.toLowerCase())),
    [nominas, search]
  );

  const totalMonto = nominas.reduce((sum, n) => sum + n.monto, 0);
  const pendientes = nominas.filter((n) => n.estado === 'pendiente').length;

  // Handlers
  const openCreate = () => { setEditingId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (n: Nomina) => {
    setEditingId(n.id);
    setForm({
      trabajadorId: n.trabajadorId,
      periodoInicio: n.periodoInicio,
      periodoFin: n.periodoFin,
      diasTrabajados: n.diasTrabajados.toString(),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.trabajadorId || !form.diasTrabajados) return;
    try {
      const payload = {
        trabajadorId: form.trabajadorId,
        periodoInicio: form.periodoInicio,
        periodoFin: form.periodoFin,
        diasTrabajados: parseInt(form.diasTrabajados) || 0,
      };
      if (editingId) {
        const updated = await fetchWithAuth(`/nominas/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        setNominas((prev) => prev.map((n) => (n.id === editingId ? updated : n)));
        showToast('success', 'Nómina actualizada');
      } else {
        const nueva = await fetchWithAuth('/nominas/', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setNominas((prev) => [nueva, ...prev]);
        showToast('success', 'Nómina registrada', `Por ${formatMXN(nueva.monto)}`);
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
    } catch (err: any) {
      showToast('error', 'Error al guardar', err.message);
    }
  };

  const handlePagar = async (nom: Nomina) => {
    try {
      const updated = await fetchWithAuth(`/nominas/${nom.id}`, {
        method: 'PUT',
        body: JSON.stringify({ estado: 'pagada' }),
      });
      setNominas((prev) => prev.map((n) => (n.id === nom.id ? updated : n)));
      showToast('success', 'Nómina pagada', `Nómina de ${nom.trabajadorNombre} marcada como pagada.`);
    } catch (err: any) {
      showToast('error', 'Error al pagar', err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await fetchWithAuth(`/nominas/${deleteTarget.id}`, { method: 'DELETE' });
      setNominas((prev) => prev.filter((n) => n.id !== deleteTarget.id));
      showToast('success', 'Nómina eliminada');
      setDeleteTarget(null);
    } catch (err: any) {
      showToast('error', 'Error al eliminar', err.message);
    }
  };

  // Estimación de monto para el modal
  const calcMontoEstimado = () => {
    if (!form.trabajadorId || !form.diasTrabajados) return null;
    const t = trabajadores.find((tr) => tr.id === form.trabajadorId);
    if (!t) return null;
    const dias = parseInt(form.diasTrabajados) || 0;
    return t.salarioDiario * dias;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">Nóminas</h1>
            <p className="text-sm text-foreground-500 mt-0.5">{nominas.length} nóminas registradas</p>
          </div>
          <button onClick={openCreate} className="px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line mr-1.5" />Nueva nómina
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-background-50 rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-400 font-medium">Total nóminas</p>
            <p className="font-heading text-xl font-semibold text-foreground-900 mt-1">{formatMXN(totalMonto)}</p>
          </div>
          <div className="bg-background-50 rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-400 font-medium">Nóminas pagadas</p>
            <p className="font-heading text-xl font-semibold text-foreground-900 mt-1">{nominas.length - pendientes}</p>
          </div>
          <div className="bg-background-50 rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-400 font-medium">Pendientes</p>
            <p className="font-heading text-xl font-semibold text-foreground-900 mt-1">{pendientes}</p>
          </div>
        </div>

        <div className="mb-4">
          <div className="relative max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por trabajador..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
        </div>

        <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-user-received-line text-xl text-foreground-300" /></div>
              <p className="text-sm text-foreground-500">No se encontraron nóminas.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/50">
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase px-5 py-3">Trabajador</th>
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase px-5 py-3 hidden sm:table-cell">Período</th>
                    <th className="text-center text-xs font-medium text-foreground-400 uppercase px-5 py-3">Días</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Monto</th>
                    <th className="text-center text-xs font-medium text-foreground-400 uppercase px-5 py-3">Estado</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {filtered.map((n) => (
                    <tr key={n.id} className="hover:bg-background-50/50 transition-colors">
                      <td className="px-5 py-3"><p className="text-sm font-medium text-foreground-800">{n.trabajadorNombre}</p></td>
                      <td className="px-5 py-3 hidden sm:table-cell"><span className="text-xs text-foreground-500">{n.periodoInicio} al {n.periodoFin}</span></td>
                      <td className="px-5 py-3 text-center"><span className="text-sm text-foreground-600">{n.diasTrabajados}</span></td>
                      <td className="px-5 py-3 text-right"><span className="text-sm font-semibold text-foreground-800">{formatMXN(n.monto)}</span></td>
                      <td className="px-5 py-3 text-center">
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${n.estado === 'pagada' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {n.estado === 'pagada' ? 'Pagada' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {n.estado === 'pendiente' && (
                            <button
                              onClick={() => handlePagar(n)}
                              className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-check-line mr-1" />Pagar
                            </button>
                          )}
                          {n.estado !== 'pagada' && (
                            <>
                              <button
                                onClick={() => openEdit(n)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary-100 text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer"
                              >
                                <i className="ri-edit-line text-sm" />
                              </button>
                              <button
                                onClick={() => setDeleteTarget(n)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-sm" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Editar nómina' : 'Nueva nómina'} size="sm"
        footer={
          <>
            <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
            <button onClick={handleSave} className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${!form.trabajadorId || !form.diasTrabajados ? 'bg-background-200 text-foreground-400 cursor-not-allowed' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`} disabled={!form.trabajadorId || !form.diasTrabajados}>
              {editingId ? 'Guardar cambios' : 'Registrar nómina'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Trabajador *</label>
            <select value={form.trabajadorId} onChange={(e) => setForm({ ...form, trabajadorId: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 cursor-pointer">
              <option value="">Seleccionar trabajador...</option>
              {trabajadores.map((t) => <option key={t.id} value={t.id}>{t.nombre} — ${t.salarioDiario}/día</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Período inicio</label>
              <input type="date" value={form.periodoInicio} onChange={(e) => setForm({ ...form, periodoInicio: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Período fin</label>
              <input type="date" value={form.periodoFin} onChange={(e) => setForm({ ...form, periodoFin: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Días trabajados *</label>
            <input type="number" value={form.diasTrabajados} onChange={(e) => setForm({ ...form, diasTrabajados: e.target.value })} placeholder="Ej: 14" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
            {form.trabajadorId && form.diasTrabajados && calcMontoEstimado() !== null && (
              <p className="text-xs text-foreground-500 mt-2">
                Monto estimado: <strong className="text-foreground-700">{formatMXN(calcMontoEstimado()!)}</strong>
              </p>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} title="Eliminar nómina" message={`¿Estás seguro de eliminar la nómina de "${deleteTarget?.trabajadorNombre}"?`} confirmLabel="Sí, eliminar" variant="danger" />
    </DashboardLayout>
  );
}